import {
  Badge,
  Button,
  Group,
  MultiSelect,
  Pagination,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { PiDownloadSimple, PiPlus } from 'react-icons/pi';
import { useLocation } from 'wouter';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import {
  GenericTable,
  type GenericTableColumn,
} from '@/components/GenericTable/GenericTable';
import { OwnerFilter } from '@/components/OwnerFilter/OwnerFilter';
import { UserAvatar } from '@/components/UserAvatar';
import { useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { useBreadcrumbs } from '@/lib/breadcrumbs';
import { formatDate } from '@/lib/helpers';
import { urlField } from '@/lib/urlState';
import { LEAD_STATUSES } from '@/models/leads';
import { useHasAnyPermission } from '@/models/permissions';
import {
  PERSON_STATE_COLORS,
  PERSON_STATE_LABELS,
  type PersonListRow,
  type PersonSort,
  type PersonStateCode,
  usePersonList,
} from '@/models/persons';
import { CreateLeadModal } from '@/pages/leads/CreateLeadModal';
import { ImportMondayLeadModal } from '@/pages/leads/ImportMondayLeadModal';

type Row = PersonListRow & {
  name: string;
  owner: string;
  created: string;
};

const SORT_FIELDS: Record<string, PersonSort> = {
  name: 'name',
  state: 'state',
  owner: 'owner',
  created: 'created',
};

const STATES = Object.keys(PERSON_STATE_LABELS) as PersonStateCode[];

const SCHEMA = {
  view: urlField.enum(['my', 'unassigned', 'all'] as const, 'my'),
  search: urlField.string(),
  state: urlField.stringArray(STATES),
  stage: urlField.stringArray(),
  owners: urlField.numberArray(),
  sortBy: urlField.enum(['', 'name', 'state', 'owner', 'created'] as const, ''),
  sortOrder: urlField.enum(['asc', 'desc'] as const, 'desc'),
  page: urlField.page(),
  limit: urlField.number(50, { integer: true, min: 1, max: 200 }),
};

export default function PeoplePage() {
  const [, nav] = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([{ title: 'Contacts', to: '/people' }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);
  const canListAll = useHasAnyPermission([
    'offers:access-all',
    'offers:*',
    '*:*',
  ]);
  const canWrite = useHasAnyPermission(['leads:write', 'leads:*', '*:*']);
  const [state, setState, { clear, isFiltered }] = useUrlState(SCHEMA);
  const [searchInput, setSearchInput] = useUrlDraft(state.search, (search) =>
    setState({ search, page: 1 }),
  );
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const view = canListAll ? state.view : 'my';
  const { data, isLoading } = usePersonList({
    view: view === 'my' ? 'my' : 'all',
    members: view === 'unassigned' ? 'none' : undefined,
    search: state.search,
    state: state.state,
    stage: state.stage,
    ownerUserId: state.owners,
    sort: state.sortBy ? SORT_FIELDS[state.sortBy] : undefined,
    dir: state.sortOrder,
    page: state.page,
    pageSize: state.limit,
  });

  const records: Row[] = (data?.rows ?? []).map((r) => ({
    ...r,
    name:
      [r.firstName, r.lastName].filter(Boolean).join(' ') ||
      r.email ||
      `#${r.id}`,
    owner: r.members.map((m) => m.name).join(', '),
    created: r.openLeadCreatedAt ?? r.createdAt,
  }));

  const handleSort = (field: string) => {
    if (!SORT_FIELDS[field]) return;
    setState({
      sortBy: field as typeof state.sortBy,
      sortOrder:
        state.sortBy === field && state.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    });
  };

  const dash = (
    <Text fz="xs" c="dimmed">
      -
    </Text>
  );

  const columns: GenericTableColumn<Row>[] = [
    {
      accessor: 'name',
      title: 'Name',
      sortable: true,
      wrap: true,
      maxWidth: 280,
      render: (r) => (
        <Text fz="sm" fw={500}>
          {r.name}
        </Text>
      ),
    },
    {
      accessor: 'email',
      title: 'Email',
      render: (r) => (r.email ? <Text fz="xs">{r.email}</Text> : dash),
    },
    {
      accessor: 'phone',
      title: 'Phone',
      render: (r) => (r.phone ? <Text fz="xs">{r.phone}</Text> : dash),
    },
    {
      accessor: 'state',
      title: 'State',
      sortable: true,
      render: (r) => (
        <Group gap={4} wrap="nowrap">
          <Badge size="sm" color={PERSON_STATE_COLORS[r.state]}>
            {PERSON_STATE_LABELS[r.state]}
          </Badge>
          {r.state === 'closed' && r.closedCode && (
            <Text fz="xs" c="dimmed">
              {[r.closedCode, r.closedReason].filter(Boolean).join(': ')}
            </Text>
          )}
        </Group>
      ),
    },
    {
      accessor: 'owner',
      title: 'Owner',
      sortable: true,
      render: (r) =>
        r.members.length ? (
          <Group gap={4} wrap="nowrap">
            {r.members.map((m) => (
              <UserAvatar
                key={m.userId}
                name={m.name}
                email={m.email}
                picture={m.picture}
                size={24}
              />
            ))}
          </Group>
        ) : (
          dash
        ),
    },
    {
      accessor: 'created',
      title: 'Created',
      sortable: true,
      render: (r) => <Text fz="xs">{formatDate(r.created)}</Text>,
    },
  ];

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / state.limit);

  return (
    <Paper p="md" h="100%">
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600} fz="lg">
            Contacts
          </Text>
          {canWrite && (
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                leftSection={<PiDownloadSimple size={14} />}
                onClick={() => setImporting(true)}
              >
                Import lead from Monday
              </Button>
              <Button
                size="xs"
                leftSection={<PiPlus size={14} />}
                onClick={() => setCreating(true)}
              >
                New Contact
              </Button>
            </Group>
          )}
        </Group>

        <Group gap="xs">
          {canListAll && (
            <SegmentedControl
              size="xs"
              value={view}
              onChange={(v) =>
                setState({ view: v as typeof state.view, page: 1 })
              }
              data={[
                { label: 'My', value: 'my' },
                { label: 'Unassigned', value: 'unassigned' },
                { label: 'All', value: 'all' },
              ]}
            />
          )}
          <TextInput
            size="xs"
            w={260}
            placeholder="Name, email, phone, Telegram, Monday ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
          />
          <MultiSelect
            size="xs"
            w={200}
            placeholder={state.state.length ? undefined : 'State'}
            data={STATES.map((value) => ({
              value,
              label: PERSON_STATE_LABELS[value],
            }))}
            value={state.state}
            onChange={(v) =>
              setState({ state: v as PersonStateCode[], page: 1 })
            }
            clearable
          />
          <MultiSelect
            size="xs"
            w={220}
            placeholder={state.stage.length ? undefined : 'Stage'}
            data={LEAD_STATUSES.filter((s) => s !== 'converted')}
            value={state.stage}
            onChange={(stage) => setState({ stage, page: 1 })}
            clearable
          />
          <OwnerFilter
            value={state.owners}
            onChange={(owners) => setState({ owners, page: 1 })}
            w={220}
          />
          <ClearFiltersButton visible={isFiltered} onClick={clear} />
        </Group>

        <GenericTable
          records={records}
          columns={columns}
          loading={isLoading}
          sortBy={state.sortBy || undefined}
          sortOrder={state.sortOrder}
          onSort={handleSort}
          emptyMessage="No contacts found"
          onRowClick={(row) => {
            const r = row as Row;
            nav(
              r.openLeadId ? `~/leads/${r.openLeadId}` : `~/contacts/${r.id}`,
            );
          }}
        />

        {totalPages > 1 && (
          <Group justify="center">
            <Pagination
              size="sm"
              total={totalPages}
              value={state.page}
              onChange={(page) => setState({ page })}
            />
          </Group>
        )}
      </Stack>
      <CreateLeadModal opened={creating} onClose={() => setCreating(false)} />
      <ImportMondayLeadModal
        opened={importing}
        onClose={() => setImporting(false)}
      />
    </Paper>
  );
}
